import { jwtDecode } from 'jwt-decode';
import { SessionContext } from '../types/sessionContext';
import {
  browserSupportsWebAuthn,
  RegistrationResponseJSON,
  startAuthentication,
  startRegistration,
  WebAuthnAbortService,
} from '@simplewebauthn/browser';

const apiBase = import.meta.env.ECOS_API_BASE;

export const storeAdminToken = (token: string): void => {
  localStorage.setItem('admin-token', token);
};

export const storeToken = (token: string): void => {
  localStorage.setItem('auth-token', token);
};

export const hasToken = (): boolean => {
  return getToken() !== null;
};

export const getAdminToken = (): string | null => {
  return localStorage.getItem('admin-token');
};

export const getToken = (): string | null => {
  return localStorage.getItem('auth-token');
};

export const getTokenData = (): SessionContext => {
  const token = getToken();
  if (!token) {
    throw new Error('No token data available');
  }
  return jwtDecode(token);
};

type TokenPayload = {
  token: string;
  shouldPromptForPasskeyRegistration?: boolean;
};

const isTokenPayload = (data: any): data is TokenPayload => {
  return 'token' in data;
};

type ErrorFormat = {
  errors: { message: string }[];
};
const isErrorFormat = (data: any): data is ErrorFormat => {
  return (
    'errors' in data && data.errors instanceof Array && data.errors.length > 0
  );
};

export const goToHomePage = () => {
  const sessionContext = getTokenData();

  if (!sessionContext.modes) {
    console.warn('No modes found in token data', sessionContext);
    logout();
    return;
  }

  if (sessionContext.modes.includes('ADMIN')) {
    window.location.replace('/admin/users');
  } else if (sessionContext.modes.includes('WATER_SUPPLIER')) {
    window.location.replace('/water-supplier/');
  } else if (sessionContext.modes.includes('COMPANY')) {
    window.location.replace('/company/jobs');
  } else if (
    sessionContext.modes.includes('INDIVIDUAL') &&
    window.location.pathname !== '/individual/jobs'
  ) {
    window.location.replace('/individual/jobs');
  }
};

const loginHandler = async (res: Response) => {
  const data = await res.json();
  if (res.status >= 400) {
    if (data.message) {
      throw new Error(data.message);
    }
  }

  if (data && isTokenPayload(data)) {
    const existingToken = getToken();
    if (existingToken) {
      storeAdminToken(existingToken);
    }

    storeToken(data.token);

    if (
      browserSupportsWebAuthn() &&
      !JSON.parse(localStorage.getItem('has-passkey-auth') || 'false')
    ) {
      try {
        await registerPasskey();
      } catch (err) {
        console.error(err);
      }
    }

    goToHomePage();
    return;
  }
  if (data && isErrorFormat(data)) {
    throw new Error(data.errors[0].message);
  }

  console.error('Login response', data);
  throw new Error('Invalid login response');
};

export const login = (email: string, password: string): Promise<void> => {
  return fetch(`${apiBase}/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  }).then(loginHandler);
};

export const loginWithGoogle = (credential: {
  code?: string;
  credential?: string;
}): Promise<void> => {
  return fetch(`${apiBase}/auth/google`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credential),
  }).then(loginHandler);
};

export const registerPasskey = async (silent: boolean = false) => {
  const token = getToken();
  if (!token) {
    throw new Error('Not logged in');
  }

  WebAuthnAbortService.cancelCeremony();

  const registrationOptionsResponse = await fetch(
    `${apiBase}/auth/webauthn/registration-options`,
    {
      headers: {
        authorization: token ? `Bearer ${token}` : '',
      },
    }
  ).then(async (res) => await res.json());

  localStorage.setItem('has-passkey-auth', 'true');

  const { optionsJSON, challengeToken } = registrationOptionsResponse;

  const attResp = await startRegistration({
    optionsJSON,
    useAutoRegister: silent,
  });

  const verificationResp = await fetch(
    `${apiBase}/auth/webauthn/verify-registration`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify({
        response: attResp,
        challengeToken,
      }),
    }
  );

  const verificationJSON = await verificationResp.json();
  return verificationJSON;
};

export const authenticateWithPasskey = async (autofill: boolean) => {
  WebAuthnAbortService.cancelCeremony();

  const resp = await fetch(`${apiBase}/auth/webauthn/authentication-options`, {
    method: 'GET',
  });
  const { optionsJSON, challengeToken } = await resp.json();

  const asseResp = await startAuthentication({
    optionsJSON,
    useBrowserAutofill: autofill,
  });

  const loginResponse = await fetch(
    `${apiBase}/auth/webauthn/verify-authentication`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        response: asseResp,
        challengeToken,
      }),
    }
  );

  localStorage.setItem('has-passkey-auth', 'true');
  loginHandler(loginResponse);
};

export const impersonate = <T>(userId: string | number): Promise<void> => {
  const token = getToken();
  if (!token) {
    throw new Error('Not logged in');
  }

  return fetch(`${apiBase}/impersonate?userId=${encodeURIComponent(userId)}`, {
    headers: {
      authorization: token ? `Bearer ${token}` : '',
    },
  }).then(loginHandler);
};

export const isImpersonating = () => !!getAdminToken();

export const logout = (): void => {
  localStorage.removeItem('auth-token');
  const adminToken = getAdminToken();
  if (adminToken) {
    storeToken(adminToken);
    localStorage.removeItem('admin-token');
    window.location.href = '/admin/users';
    return;
  }

  window.location.href = '/login';
};

const forgotPasswordResHandler = async (res: Response) => {
  const responseJson = await res.json();
  if (res.status >= 400) {
    if (responseJson.message) {
      throw new Error(responseJson.message);
    }
  }
  const { errors } = responseJson;
  if (errors?.length > 0) {
    throw new Error(errors[0].message);
  }
  return;
};

export const sendForgotPasswordEmail = (email: string): Promise<void> => {
  return fetch(`${apiBase}/password-reset-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  }).then(forgotPasswordResHandler);
};

export const sendForgottenPasswordUpdate = (payload: object): Promise<void> => {
  return fetch(`${apiBase}/reset-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  }).then(forgotPasswordResHandler);
};
